// 207C §5 — SETTINGS → TAXONOMIES: WHAT IS TYPED IS WHAT IS KEPT (207B §5's
// rule: saves leave in the order typed, one in flight, and no answer from the
// server replaces what was typed after its request left).
//
// 207B's census found the defect's third mechanism here: every editor a list
// entry opens (src/views/SettingsTaxonomies.tsx — the custom field's options,
// the Bento template's columns, the segment's values, the playbook's tasks,
// the subtask templates) writes the entry's WHOLE meta on each keystroke — a
// PATCH, then a reload of every list — and its fields are drawn from the
// reloaded list, with no copy of their own. So a key typed while an answer is
// out is written over the meta the page last HAD, and the reload that follows
// an earlier answer puts the older text back under the caret.
//
// THE CURE IS 207B §5's, not a commit on leaving the field (the label cells'
// way), because the editors write whole metas: every control in them — a tick,
// a list, a row added — composes over the meta, and a field left for a tick
// beside it would still hand the tick the old text. So: ONE SAVE QUEUE PER
// ENTRY (src/lib/saveQueue.mjs — one PATCH in flight, the latest meta waiting,
// a whole meta replacing the one before it), outliving the pane as the firm
// settings' queue does; and the page lays what was typed OVER the entry
// (SettingsTaxonomies's `typedMeta`) until the queue is idle AND a reload that
// STARTED after its last answer has landed (`reloadFresh` — a reload already
// in flight may have left before that answer). Then the entry's own meta is
// what was typed, and the overlay goes.
import type { Dispatch } from "react";
import { api } from "../api";
import { reloadFresh } from "../hooks/useTaxonomy";
import { createSaveQueue } from "./saveQueue.mjs";

type Meta = Record<string, unknown>;

const queues = new Map<number, ReturnType<typeof createSaveQueue<Meta, unknown>>>();
const settledListeners = new Set<Dispatch<number>>();

/** Queue an entry's whole meta; settles with the answer to the PATCH that carried it (a later meta may carry it). */
export function saveTaxonomyMeta(kind: string, id: number, meta: Meta): Promise<unknown> {
  const have = queues.get(id);
  if (have) return have.run(meta);
  const queue = createSaveQueue<Meta, unknown>(
    (m: Meta) => api.patch("/taxonomies/" + String(id), { meta: m }),
    (_waiting: Meta | null, next: Meta) => next,
    () => {
      void reloadFresh(kind).then(() => {
        if (queue.inflight() !== null || queue.waiting() !== null) return;
        for (const l of settledListeners) l(id);
      });
    },
  );
  queues.set(id, queue);
  return queue.run(meta);
}

/** Told the entry's id once nothing of it is on the wire or waiting, and the list reloaded after its last answer holds it. */
export function onTaxonomyMetaSettled(listener: Dispatch<number>): () => void {
  settledListeners.add(listener);
  return () => {
    settledListeners.delete(listener);
  };
}
